/**
 * Copy and video ids for the Ambient Scribe purchase placeholder (SN-19608).
 *
 * Kept in its own module so a copy change is a one-file diff and the strings are
 * importable by the spec — the tests assert against these constants rather than
 * repeating the sentences, so a reworded bullet cannot silently pass.
 */
import { NON_BREAKABLE_SPACE } from "constants/common";

export const AMBIENT_SCRIBE_DEMO_LOOM_ID = "034eea384e8749bfbb285db882368c37";

export const AMBIENT_SCRIBE_DEMO_EMBED_URL = `https://www.loom.com/embed/${AMBIENT_SCRIBE_DEMO_LOOM_ID}`;

/**
 * The share page, linked from the card caption. Some clinic networks block
 * loom.com in an iframe while allowing a normal tab, so this is the escape hatch
 * that keeps the demo reachable when the embed renders blank.
 */
export const AMBIENT_SCRIBE_DEMO_SHARE_URL = `https://www.loom.com/share/${AMBIENT_SCRIBE_DEMO_LOOM_ID}`;

export const AMBIENT_SCRIBE_PLACEHOLDER_BULLETS: readonly string[] = [
  "Notes ~90% done before you leave the room — your job is a quick review",
  "After-visit summaries that keep patients informed and engaged",
  "Built into Suno — reads patient data directly, no copy-paste, no bolt-on app",
  "Your templates, preserved — output comes back in your format",
  "Specifically built for audiology workflows",
  "HIPAA-compliant and secure"
];

const SUPPORT_EMAIL = "help@withsuno.com";

export const AMBIENT_SCRIBE_PLACEHOLDER_COPY = {
  eyebrow: "Suno Scribes",
  heading: "Your notes, done before you finish the visit.",
  subheading:
    "AI-powered clinical documentation built right into Suno — made for audiology.",
  body: "Meet Suno Scribes, your AI assistant for effortless documentation. Record the visit once — Scribes transcribes it, structures it into a ready-to-use note in your template, and drafts an after-visit summary you can hand to your patient before they leave the room. Finish charting faster, and keep every patient in the loop.",

  videoLabel: "Suno Scribe Demo Video",
  videoCaption: "See a quick walkthrough of Suno Scribes",
  videoIframeTitle: "Suno Scribes demo video",

  /** Prepended to `trialBand` only while the card checkout still comes with a trial. */
  trialBandLeadIn: "Start free for 30 days. ",
  trialBand: `Suno Scribes listens to you and your patient, so you can be fully present — not${NON_BREAKABLE_SPACE}scrambling to catch up on notes, and not leaving patients guessing what happened at their visit.`,

  ctaTrial: `Start Your Free 30\u2011Day${NON_BREAKABLE_SPACE}Trial`,
  ctaPurchase: "Purchase Subscription",

  licensesLabel: `Number of${NON_BREAKABLE_SPACE}Licenses`,
  priceLabel: "Monthly Subscription",
  /** Shown in place of a price when the licence count is empty. */
  priceUnavailable: "—",
  /** Under the price while the trial without a card is on: the checkout buys one licence. */
  priceForOneLicense: "for one license",

  /**
   * "How to purchase", opened by Purchase Subscription when the practice has no billing set up
   * (no Stripe customer), which only Suno support can do. Only a checkout that charges at once
   * needs one up front: without it, a checkout that comes with a trial reaches the backend,
   * which starts the trial without a card. Plain words for a non-technical reader: no "Stripe",
   * no "configure", and every step is something they or support actually do.
   */
  supportEmail: SUPPORT_EMAIL,
  purchaseHelpTitle: "How to purchase Suno Scribes",
  purchaseHelpLead:
    "Your practice's billing isn't set up in Suno yet, so Suno Scribes can't be purchased here. Suno support will set it up for you:",
  purchaseHelpStepEmail: "Email Suno support at",
  /** The price only when the screen has one: a failed pricing read must not print "$0". */
  purchaseHelpStepLicenses: (price?: string) =>
    `Tell them how many people at your practice will record visits with Suno Scribes. Each person needs their own license${price ? `, at ${price} a month` : ""}.`,
  purchaseHelpStepActivate:
    "Suno support sets up billing for your practice and activates your licenses.",
  purchaseHelpEmailAction: "Email Suno support",
  purchaseHelpEmailSubject: (practiceName?: string) =>
    practiceName
      ? `Purchase Suno Scribes for ${practiceName}`
      : "Purchase Suno Scribes",
  purchaseHelpEmailBody: (practiceName?: string) =>
    [
      "Hello Suno support,",
      "",
      practiceName
        ? `We would like to purchase Suno Scribes for ${practiceName}.`
        : "We would like to purchase Suno Scribes.",
      "",
      "Number of licenses: ",
      "",
      "Thank you."
    ].join("\n"),
  reasonNoPricing:
    "Pricing could not be loaded right now. Please refresh the page, or contact Suno support if this keeps happening.",
  reasonNoLicenses: "Enter how many licenses you need to continue.",

  /**
   * The support modal. Shown when the purchase endpoint answers 400 — i.e. the practice
   * cannot subscribe for a reason only Suno support can clear. `lead` is the backend's own
   * sentence, passed through verbatim; everything here is the frame around it. The body must
   * read true under every lead — including "you don't have a license" shown to a front-desk user
   * who can never hold one — so it names who to contact, not what they will do.
   */
  supportModalTitle: "Contact Suno support",
  supportModalBody: `Contact Suno support at ${SUPPORT_EMAIL}.`,
  supportModalReference: (subscriptionId: number) =>
    `Reference: subscription #${subscriptionId}`,
  supportModalAcknowledge: "Got it",

  /**
   * The trial without a credit card (SN-19868): no licence count, the price as information
   * only, and the trial length the backend reports rather than a fixed 30 days.
   */
  ctaNoCardTrial: (days: number) =>
    `Start Your Free ${days}‑Day${NON_BREAKABLE_SPACE}Trial`,
  /** When the backend refused, so there is no trial length to show. */
  ctaNoCardTrialUnavailable: `Start Your Free${NON_BREAKABLE_SPACE}Trial`,
  trialBandLeadInDays: (days: number) => `Start free for ${days} days. `,
  /** The pill beside the eyebrow while a trial is on offer. */
  heroBadge: (days: number) => `${days}-day free trial · No credit card`,
  /**
   * Beside the price while a trial is on offer (under the button where the row has no room): the
   * reassurance, then what happens at its end.
   */
  noCardRequired: "No credit card required",
  nothingChargedAtTrialEnd: "Nothing is charged when your trial ends.",
  /** The trial started, but this screen could not load it, so nothing will replace it. */
  reasonTrialStartedReload:
    "Your trial has started. Refresh the page to begin using Scribe.",
  priceAfterTrial: "per license, if you subscribe after your trial",
  /** Stripe sent the user back and the purchase is not live yet, so a second payment is blocked. */
  reasonFinishingPurchase: "Finishing your purchase…"
} as const;

/**
 * "Purchase Suno Scribes", the licence-count dialog the CTA opens while the trial without a card
 * is on (SN-19868). Written for a non-technical reader: what a licence is, who gets one, and what
 * is charged when.
 */
export const PURCHASE_LICENSES_COPY = {
  title: "Purchase Suno Scribes",
  question: "How many people at your practice will record visits?",
  questionHint: "Each person needs their own license.",
  countLabel: "Number of licenses",
  decrease: "One license fewer",
  increase: "One license more",
  invalidCount: "Enter a number of licenses from 1 to 999.",

  peopleHeading: "People who tried Suno Scribes",
  columnName: "Name",
  columnRole: "Role",
  columnTrial: "Free trial",
  columnAfterPay: "After you pay",
  you: "(you)",
  inTrial: (ends?: string) => (ends ? `In trial · ends ${ends}` : "In trial"),
  trialEnded: (ended?: string) =>
    ended ? `Trial ended ${ended}` : "Trial ended",
  noTrial: "No free trial",
  getsLicense: "License",
  noLicense: "No license",
  orderNote:
    "Licenses go to people in this order. Suno support can move a license to someone else or assign extra licenses.",
  fewerThanPeople: (missing: number) =>
    `${missing} ${missing === 1 ? "person" : "people"} on this list won't have a license and can't use Suno Scribes.`,
  moreThanPeople: (spare: number) =>
    `${spare} ${spare === 1 ? "license" : "licenses"} won't go to anyone yet. Suno support can assign ${spare === 1 ? "it" : "them"}.`,
  summaryUnavailable:
    "The list of people who tried Suno Scribes couldn't be loaded. You can still choose how many licenses to buy.",
  loadingPeople: "Loading who tried Suno Scribes…",

  total: (count: number, unitPrice: string, total: string) =>
    `${count} ${count === 1 ? "license" : "licenses"} × ${unitPrice} = ${total} a month`,
  totalUnavailable: "—",
  chargedToday: "Charged today, then every month.",
  trialTerms: (days: number, total: string) =>
    `Free for ${days} days, then ${total} a month. Your card won't be charged today.`,

  purchaseFailed:
    "The payment page couldn't be opened, and nothing was charged. Please try again.",
  cancel: "Cancel",
  continue: "Continue to payment"
} as const;

/** The snackbars after Stripe sends the user back (`ScribePurchaseReturn`). */
export const SCRIBE_PURCHASE_RETURN_COPY = {
  complete: "Your Suno Scribes purchase is complete.",
  slow: "Your payment went through. If Scribe isn't on in a few minutes, contact Suno support."
} as const;
